import { apiService } from '../../../shared/services/apiServices';
import { AxiosError } from 'axios';
import { ADMIN_ERROR_MESSAGES } from '../errors';

// ============================================================================
// API Response Types
// ============================================================================

export type ViewAdminUserRole =
  | 'SUPER_ADMIN'
  | 'ADMIN'
  | 'CLIENT'
  | 'FACILITY_USER'
  | 'TECHNICIAN';

export type ViewAdminUserStatus = 'active' | 'inactive';

export interface ViewAdminUserProfileUrl {
  url: string | null;
  isUploaded: boolean;
  fileName: string | null;
}

export interface ViewAdminUser {
  id: string;
  userId: string;
  cognitoSub: string | null;
  email: string;
  name: string;
  role: ViewAdminUserRole;
  status: ViewAdminUserStatus;
  tenantId: string | null;
  profileUrl: ViewAdminUserProfileUrl | null;
  phoneNumber: string | null;
  countryCode: string | null;
  city: string | null;
  state: string | null;
  zipCode: string | null;
  addressLine1: string | null;
  addressLine2: string | null;
  country: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface ViewAdminApiResponse {
  user: ViewAdminUser;
}

export interface ViewAdminResult {
  success: boolean;
  data?: ViewAdminApiResponse;
  error?: string;
  status?: number | undefined;
}

export interface ViewAdminOptions {
  userId: string;
  skipGlobalErrorRedirect?: boolean;
}

// ============================================================================
// Error Messages
// ============================================================================

export const ERROR_MESSAGES = {
  DEFAULT_ERROR: ADMIN_ERROR_MESSAGES.VIEW_USER_DEFAULT_ERROR,
  NETWORK_ERROR: ADMIN_ERROR_MESSAGES.NETWORK_ERROR,
  UNEXPECTED_ERROR: ADMIN_ERROR_MESSAGES.UNEXPECTED_ERROR,
} as const;

const extractErrorMessage = (errorData: unknown): string => {
  if (!errorData) {
    return ERROR_MESSAGES.DEFAULT_ERROR;
  }

  if (typeof errorData === 'string') {
    return errorData;
  }

  if (typeof errorData === 'object') {
    const apiError = errorData as { message?: string; error?: string };
    const errorMessage = apiError.message || apiError.error;

    if (errorMessage) {
      return errorMessage;
    }

    return ERROR_MESSAGES.DEFAULT_ERROR;
  }

  return ERROR_MESSAGES.DEFAULT_ERROR;
};

/**
 * Fetches admin/user details by userId (UUID).
 * Endpoint: GET loopay-user-management/users/{userId}
 */
export const viewAdmin = async (
  options: ViewAdminOptions
): Promise<ViewAdminResult> => {
  const { userId, skipGlobalErrorRedirect = false } = options;

  if (!userId?.trim()) {
    return {
      success: false,
      error: ERROR_MESSAGES.DEFAULT_ERROR,
    };
  }

  try {
    const result = await apiService.getAPI(
      `loopay-user-management/users/${userId.trim()}`,
      { skipGlobalErrorRedirect }
    );

    if (!result.error) {
      return {
        success: true,
        data: result.data as ViewAdminApiResponse,
      };
    } else {
      const errorData = result.data;
      const isNetworkError = result.isNetworkError;

      const errorMessage = extractErrorMessage(errorData);

      if (isNetworkError) {
        return {
          success: false,
          error: ERROR_MESSAGES.NETWORK_ERROR,
        };
      }

      return {
        success: false,
        error: errorMessage,
        status: result.status,
      };
    }
  } catch (error) {
    if (error instanceof AxiosError) {
      const errorData = error.response?.data;
      const isNetworkError = !error.response && !!error.request;

      if (isNetworkError) {
        return {
          success: false,
          error: ERROR_MESSAGES.NETWORK_ERROR,
        };
      }

      const errorMessage = extractErrorMessage(errorData);

      return {
        success: false,
        error: errorMessage,
        status: error.response?.status,
      };
    }

    return {
      success: false,
      error:
        error instanceof Error
          ? error.message
          : ERROR_MESSAGES.UNEXPECTED_ERROR,
    };
  }
};
